import styled from "@emotion/styled";
import { Box, CircularProgress, Button as MuiButton, Typography } from "@mui/material";
import type { ButtonProps } from "@mui/material/Button";
import { BlTooltip } from "Components/_Shared/BlTooltip";
import { Spacing } from "Infrastructure/Themes/Themes";
import type React from "react";

export type Props = Omit<ButtonProps, "variant"> & {
  variant?: ButtonProps["variant"] | "link";
  loading?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  tooltip?: React.ReactNode;
  colorCustom?: string;
  href?: string;
  target?: string;
  rel?: string;
  component?: React.ElementType;
};

interface StyledButtonProps extends ButtonProps {
  colorCustom?: string;
  href?: string;
  target?: string;
  rel?: string;
  component?: React.ElementType;
  $link?: boolean;
}

const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== "colorCustom" && prop !== "ownerState" && prop !== "$link",
})<StyledButtonProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: ${(props) => props.theme.spacing(Spacing.MAX)};
  height: ${(props) => props.theme.spacing(10)};

  & p {
    line-height: 1;
  }

  ${(props) =>
    props.size === "small" &&
    `
      height: ${props.theme.spacing(7)};
      padding: ${props.theme.spacing(0, Spacing.L)};

      & p {
        font-size: 0.75rem;
      }

      svg {
        font-size: 1rem;
      }
    `}
  

  ${(props) =>
    props.variant === "contained" &&
    props.color === "primary" &&
    `
      background-color: ${props.theme.palette.primary.main};
      & .MuiTypography-root {
        color: ${props.theme.palette.primary.contrastText};
      }

      &:hover {
        background-color: ${props.theme.colors.primaryHover};
      }

      svg {
        color: ${props.theme.palette.primary.contrastText};
      }

      &.Mui-disabled {
        background-color: ${props.theme.colors.primaryDisabled};

        .MuiTypography-root {
          color: ${props.theme.colors.textDisabled};
        }

        svg {
         color: ${props.theme.colors.textDisabled};
        }
      }
    `}

  ${(props) =>
    props.variant === "contained" &&
    props.color === "secondary" &&
    `
      background-color: ${props.theme.colors.white};
      border: 1px solid ${props.theme.colors.border};
      box-shadow: none;

      & .MuiTypography-root {
        color: ${props.theme.colors.textPrimary};
      }

      &:hover {
        border: 1px solid ${props.theme.colors.textPrimary};
        box-shadow: none;
      }

      svg {
        color: ${props.theme.colors.textPrimary};
      }

      &.Mui-disabled {
        background-color: transparent;
        border: 1px solid ${props.theme.colors.borderDisabled};

        .MuiTypography-root {
          color: ${props.theme.colors.textDisabled};
        }

        svg {
         color: ${props.theme.colors.textDisabled};
        }
      }
    `}

  ${(props) =>
    props.variant === "text" &&
    `
      ${
        props.color === "error" &&
        `
        & .MuiTypography-root {
          color: ${props.theme.palette.error.main};
        }

        svg {
          color: ${props.theme.palette.error.main};
        }
      `
      }

      &.MuiButton-text:hover,
      &.MuiButton-textSecondary:hover,
      &.MuiButton-textPrimary:hover {
        background-color: rgba(0, 0, 0, 0.02);
      }

      ${
        props.color === "primary" &&
        `
        & .MuiTypography-root {
          color: ${props.theme.palette.primary.main};
        }

        svg {
          color: ${props.theme.palette.primary.main};
        }

        &.MuiButton-textPrimary:hover {
          background-color: ${props.theme.palette.primary.light};
        }
      `
      }

      padding: ${props.theme.spacing(0, Spacing.L)};
      background-color: transparent;

      

      &.Mui-disabled {
        .MuiTypography-root {
          color: ${props.theme.colors.textDisabled};
        }

        svg {
          color: ${props.theme.colors.textDisabled};
        }
      }
    `}

  ${(props) =>
    props.$link &&
    `
      min-width: 0;
      padding: 0;

      &.MuiButton-text:hover,
      &.MuiButton-textSecondary:hover,
      &.MuiButton-textPrimary:hover {
        background-color: transparent;
      }

      &:hover .MuiTypography-root,
      &.Mui-focusVisible .MuiTypography-root {
        text-decoration: underline;
      }
    `}

  ${(props) =>
    props.variant === "outlined" &&
    props.color === "warning" &&
    `
      border-color: ${props.theme.palette.warning.dark};

      & .MuiTypography-root {
        color: ${props.theme.palette.warning.dark};
      }

      svg {
        color: ${props.theme.palette.warning.dark};
      }

      &:hover {
        border-color: ${props.theme.palette.warning.dark};
        background-color: ${props.theme.palette.warning.light};
      }
    `}

  ${(props) =>
    props.variant === "outlined" &&
    `
      &.Mui-disabled {
        border-color: ${props.theme.colors.borderDisabled};

        .MuiTypography-root {
          color: ${props.theme.colors.textDisabled};
        }

        svg {
          color: ${props.theme.colors.textDisabled};
        }
      }
    `}

  ${(props) =>
    props.colorCustom &&
    `
      & .MuiTypography-root {
        color: ${props.colorCustom};
      }

      svg {
        color: ${props.colorCustom};
      }
    `}

  .MuiButton-label {
    z-index: 1;
  }
`;

const StyledLoadingIconWrapper = styled(Box)`
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Button: React.FunctionComponent<Props> = (props) => {
  const { loading, startAdornment, endAdornment, tooltip, color = "primary", colorCustom, variant = "contained", ...rest } = props;
  const isDisabled = rest.disabled || loading;
  // a link is a text button without the hover background, so it reuses the text variant styles
  const isLink = variant === "link";

  const button = (
    <StyledButton
      {...rest}
      color={color}
      colorCustom={colorCustom}
      variant={isLink ? "text" : variant}
      $link={isLink}
      disableRipple={isLink || rest.disableRipple}
      disabled={isDisabled}
    >
      {startAdornment && (
        <Box mr={Spacing.M} height={20} display="flex" alignItems="center">
          {startAdornment}
        </Box>
      )}
      <Typography variant="body2" fontWeight={500} color={color}>
        {props.children}
      </Typography>
      {!loading && endAdornment && (
        <Box ml={Spacing.M} height={20} display="flex" alignItems="center">
          {endAdornment}
        </Box>
      )}
      {loading && (
        <StyledLoadingIconWrapper ml={Spacing.M}>
          <CircularProgress size={14} />
        </StyledLoadingIconWrapper>
      )}
    </StyledButton>
  );

  if (!tooltip) {
    return button;
  }

  // A disabled button fires no pointer events, so only then does the tooltip get a wrapper to listen on -
  // enabled buttons stay unwrapped to keep their layout untouched.
  return (
    <BlTooltip title={tooltip}>
      {isDisabled ? (
        <Box component="span" display="inline-flex">
          {button}
        </Box>
      ) : (
        button
      )}
    </BlTooltip>
  );
};
